import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Paper, CircularProgress, Alert } from "@mui/material";
import { Helmet } from "react-helmet";
import { Crumbs } from "../Crumbs.jsx";
import ReactMarkdown from "react-markdown";
import { InstagramEmbed } from "react-social-media-embed";

const BACKEND_HOST = process.env.REACT_APP_BACKEND_HOST;

const ContentPage = () => {
    const { slug } = useParams();
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPage = async () => {
            if (!slug) {
                setError("No slug provided");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                
                // Call backend API for /pages with slug parameter
                const response = await fetch(`${BACKEND_HOST}/pages?filters[slug][$eq]=${slug}&populate=*`);
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch page: ${response.statusText}`);
                }
                
                const data = await response.json();
                
                if (data.data && data.data.length > 0) {
                    setPage(data.data[0]);
                } else {
                    setError("Page not found");
                }
            } catch (err) {
                console.error("Error fetching page:", err);
                setError(err.message || "Failed to load page");
            } finally {
                setLoading(false);
            }
        };

        fetchPage();
    }, [slug]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Crumbs crumbs={[
                    { title: "Content" },
                    { title: slug || "Page" }
                ]} />
                <Alert severity="error">
                    <Typography variant="h6">Error</Typography>
                    <Typography>{error}</Typography>
                </Alert>
            </Box>
        );
    }

    if (!page) {
        return (
            <Box>
                <Crumbs crumbs={[
                    { title: "Content" },
                    { title: slug || "Page" }
                ]} />
                <Alert severity="info">
                    <Typography variant="h6">Page Not Found</Typography>
                    <Typography>The requested page could not be found.</Typography>
                </Alert>
            </Box>
        );
    }

    const pageTitle = page.title || "Content Page";
    const pageDescription = page.description || page.excerpt || "Content from Austin 2-Step";

    return (
        <Box>
            <Helmet>
                <title>{pageTitle} - Austin 2-Step</title>
                <meta name="description" content={pageDescription} />
                <meta charSet="utf-8" />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="twitter:title" content={pageTitle} />
                <meta property="twitter:description" content={pageDescription} />
                {slug && <link rel="canonical" href={`https://austin2step.com/content/${slug}`} />}
            </Helmet>
            
            <Crumbs crumbs={[
                { title: "Content" },
                { title: pageTitle }
            ]} />
            
            <Paper sx={{ p: 3, mt: 2 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {pageTitle}
                </Typography>
                
                {page.subtitle && (
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {page.subtitle}
                    </Typography>
                )}
                
                {page.body && (
                    <Box mt={3}>
                        <Typography 
                            variant="body1" 
                            component="div"
                            sx={{ 
                                whiteSpace: 'pre-wrap',
                                '& p': { mb: 2 },
                                '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 3, mb: 1 },
                                '& ul, & ol': { pl: 3, mb: 2 },
                                '& blockquote': { 
                                    borderLeft: '4px solid #ccc', 
                                    pl: 2, 
                                    ml: 0, 
                                    fontStyle: 'italic' 
                                }
                            }}
                        >
                            <ReactMarkdown components={
                                { InstagramEmbed: ({ url }) => <InstagramEmbed url={url} /> }
                                }>
                                {page.body}
                            </ReactMarkdown>
                        </Typography>
                    </Box>
                )}
               
                
              
            </Paper>
        </Box>
    );
};

export default ContentPage;
